import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDocument, useCollection, useFirestoreMutations } from '../../hooks/useFirestore'
import { CATEGORIES } from '../../lib/categories'
import HarptosDatePicker from './HarptosDatePicker'
import RichTextEditor from './RichTextEditor'
import './AdminEditor.css'

const EMPTY_EVENT = {
  title: '',
  description: '',
  category: 'campaign',
  location: '',
  start: { month: 'Hammer', day: 1, year: 1492 },
  end: null,
  hidden: false,
  characterIds: [],
  sortOrder: 0
}

export default function EventEditor() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isNew = id === 'new'

  const { data: existingEvent, loading } = useDocument('events', isNew ? null : id)
  const { data: characters } = useCollection('characters', { orderByField: 'sortOrder' })
  const { add, update, remove } = useFirestoreMutations('events')

  const [event, setEvent] = useState(EMPTY_EVENT)
  const [hasEndDate, setHasEndDate] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (existingEvent) {
      setEvent({ ...EMPTY_EVENT, ...existingEvent })
      setHasEndDate(!!existingEvent.end)
    }
  }, [existingEvent])

  const handleChange = (field, value) => {
    setEvent(prev => ({ ...prev, [field]: value }))
  }

  const handleCharacterToggle = (characterId) => {
    setEvent(prev => {
      const current = prev.characterIds || []
      if (current.includes(characterId)) {
        return { ...prev, characterIds: current.filter(id => id !== characterId) }
      }
      return { ...prev, characterIds: [...current, characterId] }
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError(null)

    try {
      const data = {
        ...event,
        end: hasEndDate ? event.end : null
      }

      if (isNew) {
        await add(data)
      } else {
        await update(id, data)
      }

      navigate('/admin/events')
    } catch (err) {
      setError(err.message)
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${event.title}"? This cannot be undone.`)) {
      return
    }

    setSaving(true)
    try {
      await remove(id)
      navigate('/admin/events')
    } catch (err) {
      setError(err.message)
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="container">
        <div className="loading-spinner" />
      </div>
    )
  }

  return (
    <div className="container">
      <div className="editor-header">
        <h1>{isNew ? 'New Event' : 'Edit Event'}</h1>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => navigate('/admin/events')}
        >
          ← Back
        </button>
      </div>

      <form onSubmit={handleSubmit} className="editor-form">
        {error && <div className="error-banner">{error}</div>}

        {/* WHEN */}
        <fieldset className="form-section">
          <legend>When</legend>
          <div className="form-row">
            <div className="form-group">
              <HarptosDatePicker
                label="Start Date *"
                value={event.start}
                onChange={(date) => handleChange('start', date)}
              />
            </div>
            <div className="form-group">
              <HarptosDatePicker
                label="End Date"
                value={event.end || event.start}
                onChange={(date) => handleChange('end', date)}
                disabled={!hasEndDate}
              />
              <label className="checkbox-label checkbox-inline">
                <input
                  type="checkbox"
                  checked={hasEndDate}
                  onChange={(e) => {
                    setHasEndDate(e.target.checked)
                    if (e.target.checked && !event.end) {
                      handleChange('end', { ...event.start })
                    }
                  }}
                />
                <span>Multi-day event</span>
              </label>
            </div>
          </div>
          <div className="form-group form-group-inline">
            <label htmlFor="sortOrder">Order</label>
            <input
              id="sortOrder"
              type="number"
              value={event.sortOrder || 0}
              onChange={(e) => handleChange('sortOrder', parseInt(e.target.value) || 0)}
              style={{ width: '80px' }}
            />
            <span className="help-text">For ordering multiple events on the same day (lower = earlier)</span>
          </div>
        </fieldset>

        {/* WHAT */}
        <fieldset className="form-section">
          <legend>What</legend>
          <div className="form-group">
            <label htmlFor="title">Event Title *</label>
            <input
              id="title"
              type="text"
              value={event.title}
              onChange={(e) => handleChange('title', e.target.value)}
              required
              placeholder="What happened?"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="category">Category</label>
              <select
                id="category"
                value={event.category}
                onChange={(e) => handleChange('category', e.target.value)}
              >
                {CATEGORIES.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="location">Location</label>
              <input
                id="location"
                type="text"
                value={event.location}
                onChange={(e) => handleChange('location', e.target.value)}
                placeholder="e.g., Waterdeep, Baldur's Gate"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Description</label>
            <RichTextEditor
              value={event.description}
              onChange={(html) => handleChange('description', html)}
              placeholder="Describe what happened during this event..."
            />
          </div>
        </fieldset>

        {/* WHO */}
        {characters.length > 0 && (
          <fieldset className="form-section">
            <legend>Who</legend>
            <div className="form-group">
              <label>Characters Involved</label>
              <div className="character-checkboxes">
                {characters.map(char => (
                  <label key={char.id} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={(event.characterIds || []).includes(char.id)}
                      onChange={() => handleCharacterToggle(char.id)}
                    />
                    <span>{char.name}</span>
                    <span className="char-type">({char.type?.toUpperCase()})</span>
                  </label>
                ))}
              </div>
            </div>
          </fieldset>
        )}

        <div className="form-group form-group-inline">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={event.hidden === true}
              onChange={(e) => handleChange('hidden', e.target.checked)}
            />
            <span>Hidden (Spoiler)</span>
          </label>
          <span className="help-text">Only visible in admin - hide future events</span>
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving}
          >
            {saving ? 'Saving...' : (isNew ? 'Create Event' : 'Save Changes')}
          </button>

          {!isNew && (
            <button
              type="button"
              className="btn btn-danger"
              onClick={handleDelete}
              disabled={saving}
            >
              Delete Event
            </button>
          )}
        </div>
      </form>
    </div>
  )
}
